import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import {
  ShoppingCartIcon,
  PlusIcon,
  TrashIcon,
  PaperAirplaneIcon,
  PencilIcon,
  BellIcon,
  XMarkIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

// Kept in sync with server pricing (routes/cart.js + routes/admin.js)
const PLAN_PRICES = {
  shared: { monthly: 20, yearly: 200 },
  private: { monthly: 25, yearly: 250 },
  premium: { monthly: 40, yearly: 400 }
};
const PLAN_TYPES = ['shared', 'private', 'premium'];

// Price for a single editor item. Domains use the admin-entered custom price;
// hosting plans use the fixed price table.
const getItemPrice = (item) => {
  if (item.planType === 'domain') {
    const price = Number(item.price);
    return Number.isFinite(price) ? price : 0;
  }
  return PLAN_PRICES[item.planType]?.[item.billingCycle] ?? 0;
};

const cartTotal = (items) =>
  items.reduce((sum, item) => sum + getItemPrice(item), 0);

const formatSentDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const userLabel = (u) => {
  if (!u) return 'Unknown user';
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ');
  return name ? `${name} (${u.email})` : u.email;
};

const AdminCartsPage = () => {
  const [carts, setCarts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  // Editor (build new sample cart OR edit an existing cart)
  const [editorOpen, setEditorOpen] = useState(false);
  // 'user'  -> target an existing registered customer (by id)
  // 'email' -> target an email address (existing user or a new prospect)
  const [editorTarget, setEditorTarget] = useState('user');
  const [editorUserId, setEditorUserId] = useState('');
  const [editorEmail, setEditorEmail] = useState('');
  // When editing an existing cart the target field is locked
  const [editorLock, setEditorLock] = useState(false);
  const [editorItems, setEditorItems] = useState([]);
  const [editorSaving, setEditorSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [cartsRes, usersRes] = await Promise.all([
        api.get('/api/admin/carts'),
        api.get('/api/admin/users')
      ]);
      setCarts(cartsRes.data.carts || []);
      setUsers(usersRes.data.users || []);
    } catch (error) {
      toast.error('Failed to load carts');
    } finally {
      setLoading(false);
    }
  };

  // Stable identifier used for per-row busy state and React keys
  const cartBusyKey = (cart) => (cart.isProspect ? `p:${cart.email}` : cart.user._id);

  const setItemBusy = (key, value) =>
    setBusy((prev) => ({ ...prev, [key]: value }));

  const openNewCartEditor = () => {
    setEditorTarget('user');
    setEditorUserId('');
    setEditorEmail('');
    setEditorLock(false);
    setEditorItems([{ planType: 'shared', billingCycle: 'monthly', url: '', price: '' }]);
    setEditorOpen(true);
  };

  const openEditCartEditor = (cart) => {
    if (cart.isProspect) {
      setEditorTarget('email');
      setEditorEmail(cart.email);
      setEditorUserId('');
    } else {
      setEditorTarget('user');
      setEditorUserId(cart.user._id);
      setEditorEmail('');
    }
    setEditorLock(true);
    setEditorItems(
      cart.items.map((item) => ({
        planType: item.planType,
        billingCycle: item.billingCycle,
        url: item.url || '',
        price: item.planType === 'domain' ? (item.price ?? '') : ''
      }))
    );
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditorItems([]);
    setEditorUserId('');
    setEditorEmail('');
    setEditorLock(false);
  };

  const addEditorItem = () =>
    setEditorItems((prev) => [...prev, { planType: 'shared', billingCycle: 'monthly', url: '', price: '' }]);

  const addDomainItem = () =>
    setEditorItems((prev) => [...prev, { planType: 'domain', billingCycle: 'yearly', url: '', price: '' }]);

  const updateEditorItem = (index, field, value) =>
    setEditorItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );

  const removeEditorItem = (index) =>
    setEditorItems((prev) => prev.filter((_, i) => i !== index));

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || '').trim());

  // Persist the editor cart. Returns the saved (summarized) cart on success,
  // or null on failure.
  const saveEditorCart = async () => {
    if (editorTarget === 'user' && !editorUserId) {
      toast.error('Please select a customer');
      return null;
    }
    if (editorTarget === 'email' && !isValidEmail(editorEmail)) {
      toast.error('Please enter a valid email address');
      return null;
    }
    const invalidDomain = editorItems.some(
      (item) => item.planType === 'domain' && !(Number(item.price) > 0)
    );
    if (invalidDomain) {
      toast.error('Enter a price greater than 0 for each domain name');
      return null;
    }
    setEditorSaving(true);
    try {
      const payload = {
        items: editorItems.map((item) =>
          item.planType === 'domain'
            ? {
                planType: 'domain',
                billingCycle: 'yearly',
                url: item.url,
                price: Number(item.price) || 0
              }
            : {
                planType: item.planType,
                billingCycle: item.billingCycle,
                url: item.url
              }
        )
      };
      const res =
        editorTarget === 'email'
          ? await api.put('/api/admin/prospect-carts', { email: editorEmail.trim(), ...payload })
          : await api.put(`/api/admin/carts/${editorUserId}`, payload);
      await fetchData();
      return res.data.cart;
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save cart');
      return null;
    } finally {
      setEditorSaving(false);
    }
  };

  const handleSaveOnly = async () => {
    const cart = await saveEditorCart();
    if (cart) {
      toast.success('Cart saved');
      closeEditor();
    }
  };

  const handleSaveAndSend = async () => {
    if (editorItems.length === 0) {
      toast.error('Add at least one item before sending');
      return;
    }
    const cart = await saveEditorCart();
    if (!cart) return;
    closeEditor();
    await sendCartEmail(cart, 'sample');
  };

  // Send a sample/reminder email for a cart (registered user or prospect).
  const sendCartEmail = async (cart, type) => {
    const key = cartBusyKey(cart);
    setItemBusy(key, type);
    try {
      const res = cart.isProspect
        ? await api.post('/api/admin/prospect-carts/send', { email: cart.email, type })
        : await api.post(`/api/admin/carts/${cart.user._id}/send`, { type });
      const updated = res.data.cart;
      const updatedKey = cartBusyKey(updated);
      setCarts((prev) => {
        const exists = prev.some((c) => cartBusyKey(c) === updatedKey);
        if (exists) {
          return prev.map((c) => (cartBusyKey(c) === updatedKey ? updated : c));
        }
        return [updated, ...prev];
      });
      toast.success(type === 'sample' ? 'Sample cart email sent' : 'Reminder email sent');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send email');
    } finally {
      setItemBusy(key, false);
    }
  };

  const openCarts = carts.filter((c) => c.itemCount > 0);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Cart Management - The Host Bros</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <Link to="/admin" className="text-sm text-primary-600 hover:text-primary-700 flex items-center mb-2">
                <ArrowLeftIcon className="h-4 w-4 mr-1" />
                Back to Admin Dashboard
              </Link>
              <h1 className="text-3xl font-bold text-gray-900">Cart Management</h1>
              <p className="text-gray-600 mt-2">
                Build and send sample carts, view all open carts, and send reminders.
              </p>
            </div>
            <button onClick={openNewCartEditor} className="btn-primary flex items-center self-start">
              <PlusIcon className="h-5 w-5 mr-2" />
              Build &amp; Send Sample Cart
            </button>
          </div>

          {/* Open Carts */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Open Carts</h2>
              <span className="text-sm text-gray-500">{openCarts.length} open</span>
            </div>

            <div className="p-6">
              {openCarts.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingCartIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No open carts</h3>
                  <p className="text-gray-600">
                    Build a sample cart for a customer to get started.
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {openCarts.map((cart) => {
                    const busyKey = cartBusyKey(cart);
                    const fullName = [cart.user.firstName, cart.user.lastName]
                      .filter(Boolean)
                      .join(' ');
                    return (
                    <div
                      key={busyKey}
                      className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
                    >
                      <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {cart.isProspect ? cart.email : (fullName || cart.user.email)}
                          </h3>
                          {!cart.isProspect && fullName && (
                            <p className="text-gray-600">{cart.user.email}</p>
                          )}
                          <div className="mt-1 flex flex-wrap gap-1">
                            {cart.isProspect ? (
                              <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800">
                                Prospect (not registered)
                              </span>
                            ) : (
                              cart.createdByAdmin && (
                                <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                                  Admin-created
                                </span>
                              )
                            )}
                          </div>
                          <div className="mt-2 text-sm text-gray-500 space-y-0.5">
                            {formatSentDate(cart.sampleSentAt) && (
                              <p>Sample sent: {formatSentDate(cart.sampleSentAt)}</p>
                            )}
                            {formatSentDate(cart.reminderSentAt) && (
                              <p>Reminder sent: {formatSentDate(cart.reminderSentAt)}</p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-gray-900">${cart.total}</p>
                          {cart.savings > 0 && (
                            <p className="text-sm text-green-600">Saves ${cart.savings}</p>
                          )}
                          <p className="text-sm text-gray-500">{cart.itemCount} item(s)</p>
                        </div>
                      </div>

                      {/* Items */}
                      <div className="mt-4 space-y-2">
                        {cart.items.map((item) => (
                          <div
                            key={item._id}
                            className="flex justify-between items-center bg-gray-50 rounded-md px-4 py-2 text-sm"
                          >
                            <div>
                              <span className="font-medium text-gray-900 capitalize">
                                {item.planType === 'domain' ? 'Domain Name' : `${item.planType} Hosting`}
                              </span>
                              <span className="text-gray-500 ml-2 capitalize">{item.billingCycle}</span>
                              {item.url && (
                                <span className="text-gray-500 ml-2 break-all">· {item.url}</span>
                              )}
                            </div>
                            <span className="text-gray-700 whitespace-nowrap">
                              ${item.price}/{item.billingCycle === 'yearly' ? 'yr' : 'mo'}
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* Actions */}
                      <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap gap-2 justify-end">
                        <button
                          onClick={() => openEditCartEditor(cart)}
                          className="btn-secondary flex items-center text-sm"
                          disabled={!!busy[busyKey]}
                        >
                          <PencilIcon className="h-4 w-4 mr-1" />
                          Edit
                        </button>
                        <button
                          onClick={() => sendCartEmail(cart, 'sample')}
                          className="btn-secondary flex items-center text-sm"
                          disabled={!!busy[busyKey]}
                        >
                          <PaperAirplaneIcon className="h-4 w-4 mr-1" />
                          {busy[busyKey] === 'sample' ? 'Sending...' : 'Send Sample'}
                        </button>
                        <button
                          onClick={() => sendCartEmail(cart, 'reminder')}
                          className="btn-primary flex items-center text-sm"
                          disabled={!!busy[busyKey]}
                        >
                          <BellIcon className="h-4 w-4 mr-1" />
                          {busy[busyKey] === 'reminder' ? 'Sending...' : 'Send Reminder'}
                        </button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Editor Modal */}
      {editorOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-md bg-white mb-10">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editorLock ? 'Edit Cart' : 'Build Sample Cart'}
              </h3>
              <button onClick={closeEditor} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Recipient selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Send to</label>
              {editorLock ? (
                <p className="px-3 py-2 bg-gray-50 rounded-md text-gray-900">
                  {editorTarget === 'email'
                    ? editorEmail
                    : userLabel(users.find((u) => u._id === editorUserId)) || editorUserId}
                </p>
              ) : (
                <>
                  <div className="flex gap-4 mb-3 text-sm">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="editorTarget"
                        value="user"
                        checked={editorTarget === 'user'}
                        onChange={() => setEditorTarget('user')}
                        className="text-primary-600 focus:ring-primary-500"
                      />
                      Existing customer
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="editorTarget"
                        value="email"
                        checked={editorTarget === 'email'}
                        onChange={() => setEditorTarget('email')}
                        className="text-primary-600 focus:ring-primary-500"
                      />
                      New email address
                    </label>
                  </div>
                  {editorTarget === 'user' ? (
                    <select
                      value={editorUserId}
                      onChange={(e) => setEditorUserId(e.target.value)}
                      className="input-field"
                    >
                      <option value="">Select a customer...</option>
                      {users.map((u) => (
                        <option key={u._id} value={u._id}>
                          {userLabel(u)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <>
                      <input
                        type="email"
                        value={editorEmail}
                        onChange={(e) => setEditorEmail(e.target.value)}
                        placeholder="customer@example.com"
                        className="input-field"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        They'll get an email to create an account with this address and
                        check out the cart. If they already have an account, it goes
                        straight to their cart.
                      </p>
                    </>
                  )}
                </>
              )}
            </div>

            {/* Items */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">Items</label>
                <div className="flex items-center gap-4">
                  <button
                    onClick={addEditorItem}
                    className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add hosting plan
                  </button>
                  <button
                    onClick={addDomainItem}
                    className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add domain name
                  </button>
                </div>
              </div>

              {editorItems.length === 0 ? (
                <p className="text-sm text-gray-500 py-4 text-center border border-dashed border-gray-300 rounded-md">
                  No items. Click "Add item" to build the cart.
                </p>
              ) : (
                <div className="space-y-3">
                  {editorItems.map((item, index) =>
                    item.planType === 'domain' ? (
                      <div key={index} className="border border-indigo-200 rounded-md p-3 bg-indigo-50">
                        <div className="flex items-center justify-between mb-2">
                          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-100 text-indigo-800">
                            Domain Name
                          </span>
                          <span className="text-xs text-gray-500">Yearly subscription</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div className="sm:col-span-2">
                            <label className="block text-xs text-gray-500 mb-1">Domain URL</label>
                            <input
                              type="url"
                              value={item.url}
                              onChange={(e) => updateEditorItem(index, 'url', e.target.value)}
                              placeholder="https://example.com"
                              className="input-field"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Price (per year)</label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.price}
                                onChange={(e) => updateEditorItem(index, 'price', e.target.value)}
                                placeholder="0.00"
                                className="input-field pl-7"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="mt-3 flex justify-end">
                          <button
                            onClick={() => removeEditorItem(index)}
                            className="text-red-600 hover:text-red-700 p-2"
                            title="Remove item"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div key={index} className="border border-gray-200 rounded-md p-3 bg-gray-50">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Plan</label>
                            <select
                              value={item.planType}
                              onChange={(e) => updateEditorItem(index, 'planType', e.target.value)}
                              className="input-field capitalize"
                            >
                              {PLAN_TYPES.map((p) => (
                                <option key={p} value={p}>
                                  {p.charAt(0).toUpperCase() + p.slice(1)}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Billing</label>
                            <select
                              value={item.billingCycle}
                              onChange={(e) => updateEditorItem(index, 'billingCycle', e.target.value)}
                              className="input-field"
                            >
                              <option value="monthly">Monthly</option>
                              <option value="yearly">Yearly</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Price</label>
                            <p className="px-3 py-2 text-gray-900 font-medium">
                              ${getItemPrice(item)}
                            </p>
                          </div>
                        </div>
                        <div className="mt-3 flex items-end gap-3">
                          <div className="flex-1">
                            <label className="block text-xs text-gray-500 mb-1">
                              Website URL (optional)
                            </label>
                            <input
                              type="url"
                              value={item.url}
                              onChange={(e) => updateEditorItem(index, 'url', e.target.value)}
                              placeholder="https://example.com"
                              className="input-field"
                            />
                          </div>
                          <button
                            onClick={() => removeEditorItem(index)}
                            className="text-red-600 hover:text-red-700 p-2"
                            title="Remove item"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    )
                  )}
                </div>
              )}
            </div>

            {/* Total */}
            <div className="flex justify-between items-center py-3 border-t border-gray-200 mb-4">
              <span className="font-medium text-gray-900">Total</span>
              <span className="text-xl font-bold text-gray-900">${cartTotal(editorItems)}</span>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap justify-end gap-3">
              <button onClick={closeEditor} className="btn-secondary" disabled={editorSaving}>
                Cancel
              </button>
              <button onClick={handleSaveOnly} className="btn-secondary" disabled={editorSaving}>
                {editorSaving ? 'Saving...' : 'Save Cart'}
              </button>
              <button onClick={handleSaveAndSend} className="btn-primary flex items-center" disabled={editorSaving}>
                <PaperAirplaneIcon className="h-4 w-4 mr-1" />
                Save &amp; Send Sample Email
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminCartsPage;
